import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Typography, CircularProgress } from "@material-ui/core";
import { useStyles } from "./DeprecationPage.styles";
import AnteLogoWordmark from "assets/ante/ante-logo-wordmark.svg";
import ConnectWalletButton from "components/ConnectWallet/ConnectWalletButton";
import AnteButton from "components/AnteButton/AnteButton";
import { useWallet } from "utils/wallet";
import { getNetworkById } from "utils/utils";
import { CHAIN_EXPLORERS } from "utils/constants";
import type { ChainId } from "utils/constants";
import { useAntePools } from "hooks/antepools/useAntePools";
import { fetchOnChainPoolData } from "utils/antepools/fetchAntePoolsPublic";
import { PoolsCache } from "utils/antepools/cache";
import queryClient from "utils/queryClient";
import type { Pool } from "types";
import { formatTokenValue } from "utils/utils";
import CountdownTimer from "pages/AnteTestDetail/components/countdownTimer";
import { DeprecationNotice } from "components/deprecation-notice/DeprecationNotice";

const STAKE_METHOD_SIGNATURES_FILTER = ["0x7b0472f0", "0x7fab9e46"];

const VIEWER_QUERY_KEY = "viewer";

const DEPRECATION_WITHDRAWN_POOLS_LS_KEY =
  "ante-deprecation-withdrawn-pools-v1";

type PersistedWithdrawnPoolsMap = Record<string, string[]>;

const buildPoolStorageKey = (pool: Pool): string => {
  const chain = (pool.chainId ?? "").toLowerCase();
  const addr = (pool.antePoolAddress ?? "").toLowerCase();
  return `${chain}:${addr}`;
};

const readPersistedWithdrawnPoolsMap = (): PersistedWithdrawnPoolsMap => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(DEPRECATION_WITHDRAWN_POOLS_LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return {};
    return parsed as PersistedWithdrawnPoolsMap;
  } catch {
    return {};
  }
};

const getPersistedWithdrawnPoolKeys = (walletAccount: string): Set<string> => {
  const map = readPersistedWithdrawnPoolsMap();
  const keys = map[walletAccount.toLowerCase()] ?? [];
  return new Set(keys);
};

const persistPoolWithdrawnForAccount = (
  walletAccount: string,
  poolKey: string
): void => {
  if (typeof window === "undefined") return;
  const map = readPersistedWithdrawnPoolsMap();
  const acc = walletAccount.toLowerCase();
  const existing = new Set(map[acc] ?? []);
  if (existing.has(poolKey)) return;
  existing.add(poolKey);
  const next: PersistedWithdrawnPoolsMap = {
    ...map,
    [acc]: Array.from(existing),
  };
  window.localStorage.setItem(
    DEPRECATION_WITHDRAWN_POOLS_LS_KEY,
    JSON.stringify(next)
  );
};

const isPoolFullySettledOnChain = (pool: Pool): boolean => {
  const staked = pool.stakedBalance?.number ?? 0;
  const challenged = pool.challengedBalance?.number ?? 0;
  const pending = pool.pendingWithdrawAmount?.number ?? 0;
  return staked <= 0 && challenged <= 0 && pending <= 0;
};

const STEP5_IMAGES = {
  notVerifiedProxy: "/images/not-verified.png",
  isThisProxyMenu: "/images/is-this-proxy.png",
  verifyProxy: "/images/verify-proxy.png",
  saveProxy: "/images/save-proxy.png",
  connectWeb3: "/images/connect-web3.png",
} as const;

const SUPPORT_EMAIL_LOCAL = "help";
const SUPPORT_EMAIL_DOMAIN = "ante.finance";

function getSupportEmail(): string {
  return `${SUPPORT_EMAIL_LOCAL}@${SUPPORT_EMAIL_DOMAIN}`;
}

function hasUserPosition(pool: Pool, account: string): boolean {
  if (!account) return false;
  const lower = account.toLowerCase();
  const inStakers = pool.stakers?.some(
    (s) =>
      s.address?.toLowerCase() === lower && BigInt(s.amount ?? "0") > BigInt(0)
  );
  const inChallengers = pool.challengers?.some(
    (c) =>
      c.address?.toLowerCase() === lower && BigInt(c.amount ?? "0") > BigInt(0)
  );
  return Boolean(inStakers || inChallengers);
}

const DeprecationPage: React.FC = () => {
  const styles = useStyles();
  const viewer = new URLSearchParams(window.location.search).get(
    VIEWER_QUERY_KEY
  );
  const {
    account: walletAccount,
    networkId,
    provider,
    refreshBalance,
    disconnect,
    isNetworkSupported,
    setChain,
  } = useWallet();
  const account = viewer ?? walletAccount;
  const {
    pools: allPoolsFromHook = [],
    loadingOffChain: poolsOffChainLoading,
    loadingOnChain: poolsOnChainLoading,
  } = useAntePools(false, account, {});

  const [submittingPoolAddress, setSubmittingPoolAddress] = useState<
    string | null
  >(null);
  const [currentTimestamp, setCurrentTimestamp] = useState<
    number | undefined
  >();
  const [persistedWithdrawnPoolKeys, setPersistedWithdrawnPoolKeys] = useState<
    Set<string>
  >(() => new Set());

  const explorer =
    networkId && CHAIN_EXPLORERS[networkId as ChainId]
      ? CHAIN_EXPLORERS[networkId as ChainId]
      : null;
  const explorerAddressUrl =
    account && explorer ? `${explorer.address}${account}` : null;
  const networkLabel = networkId ? getNetworkById(networkId)?.label : null;

  const walletData = useMemo(
    () => ({
      provider,
      networkId,
      account: account ?? undefined,
      refreshBalance,
    }),
    [provider, networkId, account, refreshBalance]
  );

  const userPositionsAllNetworks = useMemo(
    () =>
      account
        ? allPoolsFromHook.filter((p) => hasUserPosition(p, account))
        : [],
    [account, allPoolsFromHook]
  );

  const positionsWithOnChain = userPositionsAllNetworks;

  useEffect(() => {
    if (!account) {
      setPersistedWithdrawnPoolKeys(new Set());
      return;
    }
    setPersistedWithdrawnPoolKeys(getPersistedWithdrawnPoolKeys(account));
  }, [account]);

  const chainsWithPools = useMemo(() => {
    const set = new Set<string>();
    allPoolsFromHook.forEach((p) => {
      if (p.chainId) set.add(p.chainId);
    });
    return Array.from(set).sort();
  }, [allPoolsFromHook]);

  const poolsByChain = useMemo(() => {
    const map = new Map<string, Pool[]>();
    allPoolsFromHook.forEach((pool) => {
      const cid = pool.chainId ?? "unknown";
      if (!map.has(cid)) map.set(cid, []);
      map.get(cid)!.push(pool);
    });
    return map;
  }, [allPoolsFromHook]);

  useEffect(() => {
    if (!provider) return;
    const getBlock = async () => {
      try {
        const { ethers } = await import("ethers");
        const web3 = new ethers.providers.Web3Provider(provider);
        const block = await web3.getBlock("latest");
        setCurrentTimestamp(block?.timestamp);
      } catch {
        setCurrentTimestamp(undefined);
      }
    };
    getBlock();
  }, [provider]);

  const refetchPool = useCallback(
    async (pool: Pool) => {
      if (!walletData.provider || !walletData.account) return;
      const updated = await fetchOnChainPoolData(walletData, pool);
      const cacheKey = PoolsCache.keys.filtered(false, walletData);
      const current = queryClient.getQueryData<Pool[]>(cacheKey);
      if (current) {
        const next = current.map((p) =>
          p.antePoolAddress === pool.antePoolAddress ? updated : p
        );
        queryClient.setQueryData(cacheKey, next);
      }
    },
    [walletData]
  );

  const markPoolWithdrawn = useCallback(
    (pool: Pool) => {
      if (!account) return;
      const key = buildPoolStorageKey(pool);
      if (!key || key === ":") return;
      persistPoolWithdrawnForAccount(account, key);
      setPersistedWithdrawnPoolKeys(getPersistedWithdrawnPoolKeys(account));
    },
    [account]
  );

  const handleUnstakeAll = useCallback(
    async (pool: Pool, isChallenger: boolean) => {
      if (!pool.unstakeAll) return;
      setSubmittingPoolAddress(pool.antePoolAddress ?? null);
      try {
        await pool.unstakeAll(isChallenger);
        refreshBalance?.();
        await refetchPool(pool);
        if (isChallenger) {
          markPoolWithdrawn(pool);
        }
      } finally {
        setSubmittingPoolAddress(null);
      }
    },
    [refreshBalance, refetchPool, markPoolWithdrawn]
  );

  const handleWithdrawStake = useCallback(
    async (pool: Pool) => {
      if (!pool.withdrawStake) return;
      setSubmittingPoolAddress(pool.antePoolAddress ?? null);
      try {
        await pool.withdrawStake();
        refreshBalance?.();
        await refetchPool(pool);
        markPoolWithdrawn(pool);
      } finally {
        setSubmittingPoolAddress(null);
      }
    },
    [refreshBalance, refetchPool, markPoolWithdrawn]
  );

  const renderPoolWithdrawalMigrationCard = (pool: Pool): JSX.Element => {
    const poolTitle = pool.anteTestName ?? "Ante Pool";
    return (
      <div
        key={`${pool.chainId}-${pool.antePoolAddress ?? pool._id}-migration`}
        className={styles.migrationCard}
      >
        <div className={styles.migrationCardHeader}>
          <div className={styles.migrationCheckIcon}>
            <span aria-hidden="true">&#10003;</span>
          </div>
          <div>
            <Typography className={styles.migrationCardTitle}>
              Withdrawal complete
            </Typography>
            <Typography className={styles.migrationPoolName}>
              {poolTitle}
            </Typography>
          </div>
        </div>
        <Typography className={styles.migrationCardBody}>
          Your funds have been returned to your wallet.
        </Typography>
        <Typography className={styles.migrationCardSubtitle}>
          Protect your assets with Ante Vaults
        </Typography>
        <Typography className={styles.migrationCardBody}>
          Ante Vaults safeguards your digital assets with social recovery and
          inheritance that the people you trust can actually use.
        </Typography>
        <Typography className={styles.migrationCardSubtitle}>
          Get started in 2 minutes:
        </Typography>
        <ol className={styles.migrationSteps}>
          <li className={styles.migrationStep}>
            <span className={styles.migrationStepNumber}>1.</span>
            <span>
              Visit{" "}
              <a
                href="https://app.ante.org/vaults"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.chainLink}
              >
                app.ante.org
              </a>
            </span>
          </li>
          <li className={styles.migrationStep}>
            <span className={styles.migrationStepNumber}>2.</span>
            <span>Connect your wallet, or sign up via email</span>
          </li>
          <li className={styles.migrationStep}>
            <span className={styles.migrationStepNumber}>3.</span>
            <span>Create a vault and deposit your funds</span>
          </li>
        </ol>
        <a
          href="https://app.ante.org/vaults"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.migrationCta}
        >
          Open Ante Vaults &rarr;
        </a>
      </div>
    );
  };

  const renderAllCleared = () => (
    <div className={styles.allClearedCard}>
      <Typography className={styles.migrationCardSubtitle}>
        Protect your assets with Ante Vaults
      </Typography>
      <Typography className={styles.migrationCardBody}>
        Ante Vaults safeguards your digital assets with social recovery and
        inheritance that the people you trust can actually use.
      </Typography>
      <Typography className={styles.allClearedTitle}>
        All positions cleared
      </Typography>
      <Typography className={styles.allClearedBody}>
        Looking for a new home for your assets? Ante Vaults protects your assets
        with social recovery and inheritance that the people you trust can
        actually use.
      </Typography>
      <Typography className={styles.migrationCardSubtitle}>
        Get started in 2 minutes:
      </Typography>
      <ol
        className={styles.migrationSteps}
        style={{ maxWidth: 280, margin: "0 auto 20px" }}
      >
        <li className={styles.migrationStep}>
          <span className={styles.migrationStepNumber}>1.</span>
          <span>
            Visit{" "}
            <a
              href="https://app.ante.org/vaults"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.chainLink}
            >
              app.ante.org
            </a>
          </span>
        </li>
        <li className={styles.migrationStep}>
          <span className={styles.migrationStepNumber}>2.</span>
          <span>Connect your wallet, or sign up via email</span>
        </li>
        <li className={styles.migrationStep}>
          <span className={styles.migrationStepNumber}>3.</span>
          <span>Create a vault and deposit your funds</span>
        </li>
      </ol>
      <a
        href="https://app.ante.org/vaults"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.migrationCta}
      >
        Get Started with Ante Vaults &rarr;
      </a>
    </div>
  );

  const renderPositions = () => {
    if (!account) return null;

    const hasPersistedWithdrawal = persistedWithdrawnPoolKeys.size > 0;

    if (poolsOffChainLoading) {
      return (
        <div className={styles.positionsLoading}>
          <CircularProgress size={24} />
          <Typography component="span" style={{ marginLeft: 8 }}>
            Loading pools…
          </Typography>
        </div>
      );
    }

    if (poolsOnChainLoading && userPositionsAllNetworks.length > 0) {
      return (
        <div className={styles.positionsLoading}>
          <CircularProgress size={24} />
          <Typography component="span" style={{ marginLeft: 8 }}>
            Loading your positions…
          </Typography>
        </div>
      );
    }

    if (positionsWithOnChain.length === 0) {
      if (hasPersistedWithdrawal) {
        return renderAllCleared();
      }
      return (
        <div className={styles.positionsEmpty}>
          No positions found for this wallet on any network.
        </div>
      );
    }

    const positionCards = positionsWithOnChain
      .map((pool) => {
        const poolStorageKey = buildPoolStorageKey(pool);
        const isPersistedWithdrawn =
          poolStorageKey !== ":" &&
          persistedWithdrawnPoolKeys.has(poolStorageKey);
        const isSubmitting =
          submittingPoolAddress === (pool.antePoolAddress ?? null);
        const chainLabel =
          getNetworkById(pool.chainId ?? "")?.label ??
          `Chain ${pool.chainId ?? "—"}`;
        const poolExplorer = pool.chainId
          ? CHAIN_EXPLORERS[pool.chainId as ChainId]
          : null;
        const contractUrl = poolExplorer?.address
          ? `${poolExplorer.address}${pool.antePoolAddress ?? ""}`
          : null;
        const isOnConnectedNetwork =
          networkId && pool.chainId?.toLowerCase() === networkId.toLowerCase();

        const stakedBalance = pool.stakedBalance?.number ?? 0;
        const challengedBalance = pool.challengedBalance?.number ?? 0;
        const pendingAmount = pool.pendingWithdrawAmount?.number ?? 0;
        const pendingAllowedTime = pool.pendingWithdrawAllowedTime ?? 0;
        const tokenSymbol = pool.token?.symbol ?? "";
        const secondsUntilUnlock =
          currentTimestamp != null && pendingAllowedTime > 0
            ? pendingAllowedTime - currentTimestamp
            : 0;
        const canWithdrawStake = pendingAmount > 0 && secondsUntilUnlock <= 0;
        const canStartWithdrawal =
          stakedBalance > 0 &&
          pendingAmount <= 0 &&
          pool.unstakeAll &&
          !isSubmitting;
        const canUnstakeChallenger =
          challengedBalance > 0 && pool.unstakeAll && !isSubmitting;
        const showsWithdrawCountdown =
          pendingAmount > 0 && secondsUntilUnlock > 0;
        const hasActionableStateOnConnectedNetwork =
          isSubmitting ||
          canStartWithdrawal ||
          (canWithdrawStake && Boolean(pool.withdrawStake)) ||
          canUnstakeChallenger ||
          showsWithdrawCountdown;

        if (isPersistedWithdrawn && isPoolFullySettledOnChain(pool)) {
          return renderPoolWithdrawalMigrationCard(pool);
        }

        if (isOnConnectedNetwork) {
          if (isPoolFullySettledOnChain(pool)) {
            return null;
          }
          if (!hasActionableStateOnConnectedNetwork) {
            return null;
          }
        }

        return (
          <div
            key={`${pool.chainId}-${pool.antePoolAddress ?? pool._id}`}
            className={styles.positionCard}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {pool.anteTestName ?? "Ante Pool"}
            </Typography>
            <div className={styles.positionRow}>
              <span className={styles.positionLabel}>Network</span>
              <span className={styles.positionValue}>{chainLabel}</span>
            </div>
            <Typography variant="caption" color="textSecondary">
              {pool.antePoolAddress}
            </Typography>

            {isOnConnectedNetwork ? (
              <>
                <div className={styles.positionRow}>
                  <span className={styles.positionLabel}>Staked</span>
                  <span className={styles.positionValue}>
                    {formatTokenValue(
                      stakedBalance > 0 ? stakedBalance.toFixed(3) : "0",
                      tokenSymbol
                    )}
                  </span>
                </div>
                <div className={styles.positionRow}>
                  <span className={styles.positionLabel}>Challenged</span>
                  <span className={styles.positionValue}>
                    {formatTokenValue(
                      challengedBalance > 0
                        ? challengedBalance.toFixed(3)
                        : "0",
                      tokenSymbol
                    )}
                  </span>
                </div>
                {pendingAmount > 0 && (
                  <div className={styles.positionRow}>
                    <span className={styles.positionLabel}>
                      Pending withdrawal
                    </span>
                    <span className={styles.positionValue}>
                      {formatTokenValue(pendingAmount.toFixed(3), tokenSymbol)}
                    </span>
                  </div>
                )}

                <div className={styles.positionActions}>
                  {canStartWithdrawal && (
                    <AnteButton
                      variant="primary2"
                      onClick={() => handleUnstakeAll(pool, false)}
                      disabled={isSubmitting}
                      className={styles.positionButton}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={20} />
                      ) : (
                        "Start Withdrawal"
                      )}
                    </AnteButton>
                  )}
                  {canWithdrawStake && pool.withdrawStake && (
                    <AnteButton
                      variant="primary1"
                      onClick={() => handleWithdrawStake(pool)}
                      disabled={isSubmitting}
                      className={styles.positionButton}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={20} />
                      ) : (
                        "Withdraw Stake"
                      )}
                    </AnteButton>
                  )}
                  {canUnstakeChallenger && (
                    <AnteButton
                      variant="primary2"
                      onClick={() => handleUnstakeAll(pool, true)}
                      disabled={isSubmitting}
                      className={styles.positionButton}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={20} />
                      ) : (
                        "Unstake All (Challenger)"
                      )}
                    </AnteButton>
                  )}
                </div>

                {showsWithdrawCountdown && (
                  <Typography
                    variant="caption"
                    display="block"
                    style={{ marginTop: 8, color: "#777" }}
                  >
                    Withdraw available in{" "}
                    <CountdownTimer duration={secondsUntilUnlock} />
                  </Typography>
                )}
              </>
            ) : (
              <div style={{ marginTop: 12 }}>
                {contractUrl && (
                  <Typography variant="body2" style={{ color: "inherit" }}>
                    <a
                      href={contractUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.chainLink}
                    >
                      View contract on {chainLabel} →
                    </a>
                  </Typography>
                )}
                {walletAccount && pool.chainId ? (
                  <AnteButton
                    variant="primary2"
                    onClick={() => setChain(pool.chainId!)}
                    className={styles.positionButton}
                    style={{ marginTop: 8 }}
                  >
                    Switch to {chainLabel}
                  </AnteButton>
                ) : (
                  <Typography variant="body2" style={{ color: "inherit" }}>
                    Connect your wallet and switch to {chainLabel} to withdraw.
                  </Typography>
                )}
              </div>
            )}
          </div>
        );
      })
      .filter(
        (node): node is JSX.Element => node !== null && node !== undefined
      );

    if (positionCards.length === 0) {
      if (hasPersistedWithdrawal) {
        const settledPersistedPools = positionsWithOnChain.filter(
          (p) =>
            persistedWithdrawnPoolKeys.has(buildPoolStorageKey(p)) &&
            buildPoolStorageKey(p) !== ":" &&
            isPoolFullySettledOnChain(p)
        );
        if (settledPersistedPools.length > 0) {
          return (
            <>
              {settledPersistedPools.map((p) =>
                renderPoolWithdrawalMigrationCard(p)
              )}
            </>
          );
        }
      }
      return (
        <div className={styles.positionsEmpty}>
          No withdrawal actions are available for your positions on the
          connected network. Use another network or the explorer steps below if
          you still have funds in a pool.
        </div>
      );
    }

    return <>{positionCards}</>;
  };

  return (
    <div className={styles.root} data-testid="deprecation-page">
      <DeprecationNotice product="Ante Tests" mode="deprecated" />
      <div className={styles.container}>
        <div className={styles.logoRow}>
          <a href="/" aria-label="Ante Protocol">
            <img src={AnteLogoWordmark} alt="Ante Protocol" height="40" />
          </a>
        </div>
        <Card className={styles.card}>
          <Typography className={styles.title}>
            This app has been deprecated
          </Typography>
          <Typography className={styles.body}>
            We advise users to withdraw their funds. You can view your positions
            and interact with the contracts directly via your block explorer
            (e.g. Etherscan, or the explorer for the network you used).
          </Typography>
          <Typography className={styles.body}>
            If you need assistance withdrawing your funds, contact the team via{" "}
            <a
              href="https://discord.gg/ante"
              target="_blank"
              rel="noopener noreferrer"
            >
              Discord
            </a>{" "}
            or{" "}
            <button
              type="button"
              onClick={() => {
                window.location.href = `mailto:${getSupportEmail()}`;
              }}
              className={styles.addressLink}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                cursor: "pointer",
              }}
            >
              {getSupportEmail()}
            </button>
          </Typography>

          <div className={styles.walletSection}>
            <Typography className={styles.walletLabel}>Wallet</Typography>
            {account && isNetworkSupported ? (
              <div className={styles.walletConnectedRow}>
                <Typography className={styles.address} component="span">
                  {account}
                </Typography>
                {explorerAddressUrl && (
                  <>
                    {" · "}
                    <a
                      href={explorerAddressUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.addressLink}
                    >
                      View on {networkLabel ?? "explorer"}
                    </a>
                  </>
                )}
                {" · "}
                <button
                  type="button"
                  onClick={() => disconnect()}
                  className={styles.disconnectButton}
                  aria-label="Disconnect wallet"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <>
                <Typography
                  className={styles.walletOptionalHint}
                  component="p"
                  variant="body2"
                >
                  You can connect a wallet to see your positions here, or follow
                  the steps below to withdraw via the block explorer.
                </Typography>
                {account && (
                  <Typography
                    className={styles.walletOptionalHint}
                    component="p"
                    variant="body2"
                  >
                    {account}
                  </Typography>
                )}
                <ConnectWalletButton text="Connect wallet" />
              </>
            )}
          </div>

          {account && (
            <div className={styles.positionsSection}>
              <Typography className={styles.positionsTitle}>
                Your positions
              </Typography>
              {renderPositions()}
            </div>
          )}

          <Typography className={styles.stepsTitle}>
            How to recover your funds
          </Typography>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li className={styles.step}>
              <Typography className={styles.stepNumber}>Step 1</Typography>
              <Typography className={styles.stepText}>
                Identify the wallet address you want to withdraw from (e.g. the
                one you used to stake).
              </Typography>
            </li>
            <li className={styles.step}>
              <Typography className={styles.stepNumber}>Step 2</Typography>
              <Typography className={styles.stepText}>
                Find the AntePools where you have staked. Open the block
                explorer for the chain you staked on. AntePools are deployed on
                the following chains (open your wallet’s transaction history on
                the explorer for the relevant chain):
              </Typography>
              <ul className={styles.chainList}>
                {chainsWithPools.length > 0 ? (
                  chainsWithPools.map((cid) => {
                    const explorer = CHAIN_EXPLORERS[cid as ChainId];
                    const label = getNetworkById(cid)?.label ?? `Chain ${cid}`;
                    const url = explorer?.base ?? "#";
                    return (
                      <li key={cid}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.chainLink}
                        >
                          {label}
                        </a>
                      </li>
                    );
                  })
                ) : (
                  <li>
                    <Typography className={styles.stepText} component="span">
                      Or open the block explorer for the chain you staked on
                      (e.g. Etherscan for Ethereum).
                    </Typography>
                  </li>
                )}
              </ul>
              <Typography
                className={styles.stepText}
                style={{ marginTop: 8, fontStyle: "italic" }}
              >
                You can double-check any contract address against the list of
                all AntePools at the bottom of this page.
              </Typography>
            </li>
            <li className={styles.step}>
              <Typography className={styles.stepNumber}>Step 3</Typography>
              <Typography className={styles.stepText}>
                On the block explorer, go to <strong>Advanced filter</strong>{" "}
                and filter by method signature. Use one of:{" "}
                <code className={styles.code}>
                  {STAKE_METHOD_SIGNATURES_FILTER[0]}
                </code>
                {" or "}
                <code className={styles.code}>
                  {STAKE_METHOD_SIGNATURES_FILTER[1]}
                </code>
                . If you have interacted with another protocol that uses the
                same function signature, you may see addresses that are not
                AntePools—check them against the list below.
              </Typography>
            </li>
            <li className={styles.step}>
              <Typography className={styles.stepNumber}>Step 4</Typography>
              <Typography className={styles.stepText}>
                From the filtered transactions, get the contract address from
                the &quot;To&quot; field. Open that address on the block
                explorer for the next step.
              </Typography>
            </li>
            <li className={styles.step}>
              <Typography className={styles.stepNumber}>Step 5</Typography>
              <Typography className={styles.stepText}>
                Open the <strong>Contract</strong> tab, then{" "}
                <strong>Write as Proxy</strong>. If you are not seeing this tab,
                you may need to verify the proxy. Follow the steps in the
                screenshots below. Then go back to{" "}
                <strong>Write as proxy</strong>, connect with Web3, and execute{" "}
                <code className={styles.code}>unstakeAll</code> with your side
                (staker or challenger).
              </Typography>
              <Typography className={styles.stepText}>
                Optional: use <strong>Read as proxy</strong> →
                <code className={styles.code}> getStoredBalance</code> to
                confirm which side you staked on.
              </Typography>
              <div className={styles.stepImagesWrap}>
                <figure className={styles.stepImageBlock}>
                  <figcaption className={styles.stepImageCaption}>
                    1. Not verified proxy — no "Write as Proxy" tab shown
                  </figcaption>
                  <img
                    src={STEP5_IMAGES.notVerifiedProxy}
                    alt="Contract page showing proxy not verified message"
                    className={styles.stepImage}
                  />
                </figure>
                <figure className={styles.stepImageBlock}>
                  <figcaption className={styles.stepImageCaption}>
                    2. Code tab → More options → Is this proxy?
                  </figcaption>
                  <img
                    src={STEP5_IMAGES.isThisProxyMenu}
                    alt="Code tab, More options, Is this proxy? menu"
                    className={styles.stepImage}
                  />
                </figure>
                <figure className={styles.stepImageBlock}>
                  <figcaption className={styles.stepImageCaption}>
                    3. Verify proxy
                  </figcaption>
                  <img
                    src={STEP5_IMAGES.verifyProxy}
                    alt="Verify proxy dialog or button"
                    className={styles.stepImage}
                  />
                </figure>
                <figure className={styles.stepImageBlock}>
                  <figcaption className={styles.stepImageCaption}>
                    4. Save proxy
                  </figcaption>
                  <img
                    src={STEP5_IMAGES.saveProxy}
                    alt="Save proxy confirmation"
                    className={styles.stepImage}
                  />
                </figure>
                <figure className={styles.stepImageBlock}>
                  <figcaption className={styles.stepImageCaption}>
                    5. Connect Web3, then Write as proxy
                  </figcaption>
                  <img
                    src={STEP5_IMAGES.connectWeb3}
                    alt="Connect Web3 to write contract"
                    className={styles.stepImage}
                  />
                </figure>
              </div>
            </li>
            <li className={styles.step}>
              <Typography className={styles.stepNumber}>Step 6</Typography>
              <Typography className={styles.stepText}>
                Wait 24 hours (unlock period).
              </Typography>
            </li>
            <li className={styles.step}>
              <Typography className={styles.stepNumber}>Step 7</Typography>
              <Typography className={styles.stepText}>
                Return to the same contract page (Write as proxy), connect Web3,
                and execute <code className={styles.code}>withdrawStake</code>{" "}
                to receive your funds.
              </Typography>
            </li>
          </ol>

          <div className={styles.allPoolsSection}>
            <Typography className={styles.subsectionTitle}>
              All AntePools (by chain)
            </Typography>
            <Typography
              className={styles.stepText}
              style={{ marginBottom: 16 }}
            >
              Use this list to verify that a contract address is an AntePool
              before interacting with it.
            </Typography>
            {chainsWithPools.length === 0 && !poolsOffChainLoading && (
              <Typography className={styles.stepText} color="textSecondary">
                No pools loaded. Refresh the page or check your connection.
              </Typography>
            )}
            {chainsWithPools.map((cid) => {
              const pools = poolsByChain.get(cid) ?? [];
              const explorer = CHAIN_EXPLORERS[cid as ChainId];
              const label = getNetworkById(cid)?.label ?? `Chain ${cid}`;
              const baseUrl = explorer?.address ?? "";
              return (
                <div key={cid} className={styles.poolListByChain}>
                  <Typography className={styles.poolListLabel}>
                    {label}
                  </Typography>
                  {pools.map((pool) => (
                    <div
                      key={pool.antePoolAddress ?? pool._id}
                      className={styles.poolAddressRow}
                    >
                      {pool.anteTestName && (
                        <Typography className={styles.poolTestName}>
                          {pool.anteTestName}
                        </Typography>
                      )}
                      <a
                        href={`${baseUrl}${pool.antePoolAddress ?? ""}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.poolAddressLink}
                      >
                        {pool.antePoolAddress}
                      </a>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};

export { DeprecationPage };
